import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faClock, faLock, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import { Icon } from "@/components";

import { CompanyLifecycleState } from "../schema/companies";

const formatDate = (value: string): string => format(new Date(value), "dd.MM.yyyy");

const STATE_STYLES: Record<CompanyLifecycleState, string> = {
	ACTIVE: "bg-status-success-light text-status-success",
	GRACE: "bg-status-warning-light text-text",
	ARCHIVED: "bg-status-error-light text-status-error",
	DUE_FOR_DELETION: "bg-status-error text-white",
};

/**
 * The lifecycle state of a company as the admin table shows it (PREC-4093).
 *
 * The state is resolved by the API with the same function that walls the app off, so this pill is
 * what the customer's guard decided — not a re-derivation from the raw dates. Twenty-two companies
 * sat locked out and unmailed for nine days because this column did not exist and the only trace
 * was one log line a day.
 */
export const LifecycleStateCell: FunctionComponent<{ state?: CompanyLifecycleState }> = ({
	state,
}) => {
	const { t } = useTranslation();

	if (!state) {
		return <span className="text-text-meta">—</span>;
	}

	return (
		<span
			className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-normal ${STATE_STYLES[state]}`}
			data-test={`lifecycle-state-${state}`}
		>
			{t(`companies.lifecycle.state.${state}`)}
		</span>
	);
};

/**
 * The dates behind the state: the end of the read-only month, the archive date, and the deletion
 * date — the last flagged when it is already past.
 *
 * An overdue deletion date is the signature of the stuck cohort: a late admission (PREC-3921) is
 * dated from the real end of service, so these companies arrive past `hardDeleteAt` having never
 * been archived. Flagged visually so a whole cohort is obvious at a glance rather than one row at
 * a time.
 */
export const LifecycleDatesCell: FunctionComponent<{
	state?: CompanyLifecycleState;
	readOnlyUntil?: string | null;
	archivedAt?: string | null;
	deletionAt?: string | null;
	now?: Date;
}> = ({ state, readOnlyUntil, archivedAt, deletionAt, now = new Date() }) => {
	const { t } = useTranslation();

	if (!state || state === "ACTIVE") {
		return <span className="text-text-meta">—</span>;
	}

	const deletionOverdue = Boolean(deletionAt && new Date(deletionAt) <= now);

	return (
		<div className="flex flex-col gap-1 text-sm text-text">
			{readOnlyUntil && state === "GRACE" && (
				<div className="flex flex-row items-center gap-2" data-test="lifecycle-read-only-until">
					<Icon icon={faClock} />
					{t("companies.lifecycle.readOnlyUntil", { date: formatDate(readOnlyUntil) })}
				</div>
			)}
			{archivedAt && (
				<div className="flex flex-row items-center gap-2" data-test="lifecycle-archived-at">
					<Icon icon={faLock} />
					{t("companies.lifecycle.archivedAt", { date: formatDate(archivedAt) })}
				</div>
			)}
			{deletionAt && (
				<div
					className={`flex flex-row items-center gap-2 ${
						deletionOverdue ? "font-medium text-status-error" : ""
					}`}
					data-test={deletionOverdue ? "lifecycle-deletion-overdue" : "lifecycle-deletion-at"}
				>
					<Icon icon={deletionOverdue ? faTriangleExclamation : faClock} />
					{t(
						deletionOverdue
							? "companies.lifecycle.deletionOverdue"
							: "companies.lifecycle.deletionAt",
						{ date: formatDate(deletionAt) }
					)}
				</div>
			)}
		</div>
	);
};
